import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Upload,
  message,
} from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiOriginForStaticAssets } from '@/config/apiBaseUrl';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationHouseSelect,
  EducationQuotaSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

const STUDENT_DOCUMENT_TYPES = [
  'birth_certificate',
  'aadhaar',
  'transfer_certificate',
  'caste_certificate',
  'income_certificate',
  'passport_photo',
  'address_proof',
  'medical_fitness',
  'other',
] as const;

const DOC_TYPE_I18N: Record<(typeof STUDENT_DOCUMENT_TYPES)[number], string> = {
  birth_certificate: 'docTypeBirthCertificate',
  aadhaar: 'docTypeAadhaar',
  transfer_certificate: 'docTypeTransferCertificate',
  caste_certificate: 'docTypeCasteCertificate',
  income_certificate: 'docTypeIncomeCertificate',
  passport_photo: 'docTypePassportPhoto',
  address_proof: 'docTypeAddressProof',
  medical_fitness: 'docTypeMedicalFitness',
  other: 'docTypeOther',
};

type Campus = { id: string; code: string; name: string };

type StudentDocument = {
  id: string;
  document_type: string;
  title: string;
  verification_status: string;
  expires_on?: string | null;
  storage_ref?: string | null;
};

type StudentDetail = {
  student: Record<string, unknown> & {
    id: string;
    admission_no: string;
    first_name: string;
    last_name: string | null;
    status: string;
    campus_id: string;
    photo_url?: string | null;
    record_version?: number;
  };
  documents?: StudentDocument[];
};

type ApiError = { response?: { data?: { message?: string } } };

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const CATEGORIES = ['General', 'OBC', 'SC', 'ST', 'EWS', 'Other'];
const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other', 'Prefer not to say'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const STATUSES = ['active', 'inactive', 'withdrawn', 'alumni', 'transferred_out'];
const ADMISSION_TYPES = ['NEW', 'OLD'];

type FormValues = {
  campus_id?: string;
  campus_code?: string;
  campus_name?: string;
  admission_no: string;
  first_name: string;
  last_name?: string | null;
  status?: string;
  date_of_birth?: dayjs.Dayjs | null;
  admission_date?: dayjs.Dayjs | null;
  gender?: string | null;
  category?: string | null;
  quota_id?: string | null;
  religion?: string | null;
  nationality?: string | null;
  blood_group?: string | null;
  aadhaar_masked?: string | null;
  preferred_language?: string | null;
  photo_url?: string | null;
  roll_no?: string | null;
  board_registration_no?: string | null;
  admission_type?: string | null;
  admission_session_id?: string | null;
  admission_grade_id?: string | null;
  tc_number?: string | null;
  stream_code?: string | null;
  house_id?: string | null;
  phone?: string | null;
  email?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state_name?: string | null;
  pincode?: string | null;
  father_name?: string | null;
  father_phone?: string | null;
  father_email?: string | null;
  father_occupation?: string | null;
  mother_name?: string | null;
  mother_phone?: string | null;
  mother_email?: string | null;
  mother_occupation?: string | null;
  guardian_name?: string | null;
  guardian_relation?: string | null;
  guardian_phone?: string | null;
  guardian_email?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relation?: string | null;
  medical_condition?: string | null;
  allergies?: string | null;
  height_cm?: number | null;
  weight_kg?: number | null;
  pen_id?: string | null;
  apaar_id?: string | null;
  previous_school?: string | null;
};

function dayOrNull(value: unknown) {
  return value ? dayjs(String(value)) : null;
}

export default function StudentFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [docForm] = Form.useForm<{
    document_type: string;
    title: string;
    verification_status?: string;
    expires_on?: dayjs.Dayjs | null;
  }>();
  const [pendingDocFile, setPendingDocFile] = useState<File | null>(null);
  const serverOrigin = apiOriginForStaticAssets();

  const campuses = useQuery({
    queryKey: ['education-campuses'],
    queryFn: () => api.get<Campus[]>('/education/campuses'),
  });

  const existing = useQuery({
    queryKey: ['education-student', id],
    queryFn: () => api.get<StudentDetail>(`/education/students/${id}`),
    enabled: isEdit,
  });

  const photoUrl = existing.data?.data?.student?.photo_url
    ? `${serverOrigin}${existing.data.data.student.photo_url}`
    : undefined;

  const photoUpload = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('photo', file);
      return api.post<{ photo_url: string }>(`/education/students/${id}/photo`, formData);
    },
    onSuccess: () => {
      message.success(t('photoUploaded'));
      queryClient.invalidateQueries({ queryKey: ['education-student', id] });
    },
    onError: (error: ApiError) => {
      message.error(error.response?.data?.message || t('unavailable', { status: '' }));
    },
  });

  const documentUpload = useMutation({
    mutationFn: async (values: {
      document_type: string;
      title: string;
      verification_status?: string;
      expires_on?: dayjs.Dayjs | null;
    }) => {
      const formData = new FormData();
      formData.append('document_type', values.document_type);
      formData.append('title', values.title);
      formData.append('verification_status', values.verification_status || 'pending');
      if (values.expires_on) formData.append('expires_on', values.expires_on.format('YYYY-MM-DD'));
      if (pendingDocFile) formData.append('file', pendingDocFile);
      return api.post(`/education/students/${id}/documents`, formData);
    },
    onSuccess: () => {
      message.success(t('documentUploaded'));
      setPendingDocFile(null);
      docForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['education-student', id] });
    },
    onError: (error: ApiError) => {
      message.error(error.response?.data?.message || t('unavailable', { status: '' }));
    },
  });

  useEffect(() => {
    const student = existing.data?.data?.student;
    if (!student) return;
    const next: FormValues = {
      campus_id: String(student.campus_id || ''),
      admission_no: String(student.admission_no || ''),
      first_name: String(student.first_name || ''),
      last_name: student.last_name ? String(student.last_name) : undefined,
      status: student.status ? String(student.status) : 'active',
      date_of_birth: dayOrNull(student.date_of_birth),
      admission_date: dayOrNull(student.admission_date),
      gender: student.gender != null ? String(student.gender) : undefined,
      category: student.category != null ? String(student.category) : undefined,
      quota_id: student.quota_id != null ? String(student.quota_id) : undefined,
      religion: student.religion != null ? String(student.religion) : undefined,
      nationality: student.nationality != null ? String(student.nationality) : undefined,
      blood_group: student.blood_group != null ? String(student.blood_group) : undefined,
      aadhaar_masked: student.aadhaar_masked != null ? String(student.aadhaar_masked) : undefined,
      preferred_language: student.preferred_language != null ? String(student.preferred_language) : undefined,
      photo_url: student.photo_url != null ? String(student.photo_url) : undefined,
      roll_no: student.roll_no != null ? String(student.roll_no) : undefined,
      board_registration_no:
        student.board_registration_no != null ? String(student.board_registration_no) : undefined,
      admission_type: student.admission_type != null ? String(student.admission_type) : undefined,
      admission_session_id:
        student.admission_session_id != null ? String(student.admission_session_id) : undefined,
      admission_grade_id: student.admission_grade_id != null ? String(student.admission_grade_id) : undefined,
      tc_number: student.tc_number != null ? String(student.tc_number) : undefined,
      stream_code: student.stream_code != null ? String(student.stream_code) : undefined,
      house_id: student.house_id != null ? String(student.house_id) : undefined,
      phone: student.phone != null ? String(student.phone) : undefined,
      email: student.email != null ? String(student.email) : undefined,
      address_line1: student.address_line1 != null ? String(student.address_line1) : undefined,
      address_line2: student.address_line2 != null ? String(student.address_line2) : undefined,
      city: student.city != null ? String(student.city) : undefined,
      state_name: student.state_name != null ? String(student.state_name) : undefined,
      pincode: student.pincode != null ? String(student.pincode) : undefined,
      father_name: student.father_name != null ? String(student.father_name) : undefined,
      father_phone: student.father_phone != null ? String(student.father_phone) : undefined,
      father_email: student.father_email != null ? String(student.father_email) : undefined,
      father_occupation: student.father_occupation != null ? String(student.father_occupation) : undefined,
      mother_name: student.mother_name != null ? String(student.mother_name) : undefined,
      mother_phone: student.mother_phone != null ? String(student.mother_phone) : undefined,
      mother_email: student.mother_email != null ? String(student.mother_email) : undefined,
      mother_occupation: student.mother_occupation != null ? String(student.mother_occupation) : undefined,
      guardian_name: student.guardian_name != null ? String(student.guardian_name) : undefined,
      guardian_relation: student.guardian_relation != null ? String(student.guardian_relation) : undefined,
      guardian_phone: student.guardian_phone != null ? String(student.guardian_phone) : undefined,
      guardian_email: student.guardian_email != null ? String(student.guardian_email) : undefined,
      emergency_contact_name:
        student.emergency_contact_name != null ? String(student.emergency_contact_name) : undefined,
      emergency_contact_phone:
        student.emergency_contact_phone != null ? String(student.emergency_contact_phone) : undefined,
      emergency_contact_relation:
        student.emergency_contact_relation != null ? String(student.emergency_contact_relation) : undefined,
      medical_condition: student.medical_condition != null ? String(student.medical_condition) : undefined,
      allergies: student.allergies != null ? String(student.allergies) : undefined,
      height_cm: student.height_cm != null ? Number(student.height_cm) : undefined,
      weight_kg: student.weight_kg != null ? Number(student.weight_kg) : undefined,
      pen_id: student.pen_id != null ? String(student.pen_id) : undefined,
      apaar_id: student.apaar_id != null ? String(student.apaar_id) : undefined,
      previous_school: student.previous_school != null ? String(student.previous_school) : undefined,
    };
    form.setFieldsValue(next);
  }, [existing.data?.data?.student, form]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      let campusId = values.campus_id;
      if (!campusId && !isEdit) {
        const created = await api.post<Campus>('/education/campuses', {
          code: values.campus_code,
          name: values.campus_name,
        });
        campusId = created.data?.id;
      }
      const {
        campus_code: _campusCode,
        campus_name: _campusName,
        photo_url: _photoUrl,
        date_of_birth,
        admission_date,
        ...rest
      } = values;
      const payload: Record<string, unknown> = {
        ...rest,
        campus_id: campusId,
        admission_no: String(values.admission_no || '').trim(),
        first_name: String(values.first_name || '').trim(),
        last_name: values.last_name ? String(values.last_name).trim() : null,
        status: values.status || 'active',
        date_of_birth: date_of_birth ? date_of_birth.format('YYYY-MM-DD') : null,
        admission_date: admission_date ? admission_date.format('YYYY-MM-DD') : null,
      };
      if (isEdit && id) {
        return api.put(`/education/students/${id}`, {
          ...payload,
          record_version: existing.data?.data?.student?.record_version,
        });
      }
      return api.post<{ id: string }>('/education/students', payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['education-students'] });
      queryClient.invalidateQueries({ queryKey: ['education-students-stats'] });
      if (isEdit && id) {
        queryClient.invalidateQueries({ queryKey: ['education-student', id] });
        message.success(t('studentUpdated'));
        navigate(`/education/students/${id}`);
        return;
      }
      message.success(t('studentSaved'));
      navigate(`/education/students/${(result.data as { id?: string })?.id}`);
    },
    onError: (error: ApiError) => {
      message.error(error.response?.data?.message || t('unavailable', { status: '' }));
    },
  });

  const options = (campuses.data?.data ?? []).map((campus) => ({
    value: campus.id,
    label: `${campus.code} — ${campus.name}`,
  }));

  return (
    <EducationPageChrome
      title={isEdit ? t('editStudent') : t('newStudent')}
      description={t('studentFormIntro', {
        defaultValue:
          'Capture demographics, admission context, family contacts, and structured health summary. Current class/section stays on Enrollment; fees/transport/library links appear when those modules are installed.',
      })}
      extra={
        <Button onClick={() => navigate(isEdit && id ? `/education/students/${id}` : '/education/students')}>
          {t('backToStudents')}
        </Button>
      }
    >
      <Card size="small" loading={isEdit && existing.isLoading}>
        <Form form={form} layout="vertical" initialValues={{ status: 'active', admission_type: 'NEW', nationality: 'Indian' }} onFinish={(values) => save.mutate(values)}>
          <Tabs
            items={[
              {
                key: 'identity',
                label: t('identityTab'),
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={6}>
                      <Card size="small" title={t('studentPhoto')} style={{ marginBottom: 16 }}>
                        <Space direction="vertical" align="center" style={{ width: '100%' }}>
                          <Avatar size={112} src={photoUrl} icon={<UserOutlined />} />
                          {isEdit ? (
                            <>
                              <Upload
                                showUploadList={false}
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                beforeUpload={(file) => {
                                  photoUpload.mutate(file);
                                  return false;
                                }}
                              >
                                <Button icon={<UploadOutlined />} loading={photoUpload.isPending}>
                                  {t('uploadPhoto')}
                                </Button>
                              </Upload>
                              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, textAlign: 'center' }}>
                                {t('photoHint')}
                              </div>
                            </>
                          ) : (
                            <Alert type="info" showIcon message={t('documentsSaveFirst')} style={{ width: '100%' }} />
                          )}
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} md={18}>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="campus_id"
                            label={t('campus')}
                            rules={[
                              {
                                validator: async (_, value) => {
                                  if (value) return;
                                  if (isEdit) throw new Error(t('campus'));
                                  const code = form.getFieldValue('campus_code');
                                  const name = form.getFieldValue('campus_name');
                                  if (code && name) return;
                                  throw new Error(t('campus'));
                                },
                              },
                            ]}
                          >
                            <Select
                              allowClear={!isEdit}
                              showSearch
                              optionFilterProp="label"
                              options={options}
                              loading={campuses.isLoading}
                            />
                          </Form.Item>
                        </Col>
                        {!isEdit ? (
                          <>
                            <Col xs={24} md={6}>
                              <Form.Item name="campus_code" label={t('campusCode')}>
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                              <Form.Item name="campus_name" label={t('campusName')}>
                                <Input />
                              </Form.Item>
                            </Col>
                          </>
                        ) : null}
                        <Col xs={24} md={8}>
                          <Form.Item name="admission_no" label={t('admissionNo')} rules={[{ required: true }]}>
                            <Input disabled={isEdit} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="roll_no" label={t('rollNo')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
                            <Select
                              options={STATUSES.map((item) => ({
                                value: item,
                                label: t(`studentStatus.${item}`, { defaultValue: item }),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="first_name" label={t('firstName')} rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="last_name" label={t('lastName')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="date_of_birth" label={t('dateOfBirth')}>
                            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="gender" label={t('gender')}>
                            <Select allowClear options={GENDERS.map((g) => ({ value: g, label: g }))} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="category" label={t('category')}>
                            <Select allowClear options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="quota_id" label={t('quota')}>
                            <EducationQuotaSelect />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="religion" label={t('religion')}>
                            <Select allowClear options={RELIGIONS.map((r) => ({ value: r, label: r }))} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="nationality" label={t('nationality')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="blood_group" label={t('bloodGroup')}>
                            <Select allowClear options={BLOOD_GROUPS.map((b) => ({ value: b, label: b }))} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="aadhaar_masked" label={t('aadhaar')}>
                            <Input placeholder="XXXX-XXXX-1234" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="preferred_language" label={t('preferredLanguage')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="pen_id" label={t('penId')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="apaar_id" label={t('apaarId')}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="board_registration_no" label={t('boardRegistrationNo')}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'admission',
                label: t('admissionTab'),
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="admission_type" label={t('admissionType', { defaultValue: 'Admission type' })}>
                        <Select options={ADMISSION_TYPES.map((v) => ({ value: v, label: v }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="admission_date" label={t('admissionDate')}>
                        <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="tc_number" label={t('tcNumber', { defaultValue: 'TC number (incoming)' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="admission_session_id" label={t('admissionSession', { defaultValue: 'Admission session' })}>
                        <EducationSessionSelect />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="admission_grade_id" label={t('admissionClass', { defaultValue: 'Admission class' })}>
                        <EducationGradeSelect />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="stream_code" label={t('stream', { defaultValue: 'Stream' })}>
                        <Input placeholder="Science / Commerce / Arts" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="house_id" label={t('house', { defaultValue: 'House' })}>
                        <EducationHouseSelect />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item name="previous_school" label={t('previousSchool')}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'contact',
                label: t('contactTab'),
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="phone" label={t('phone')}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="email" label={t('email')}>
                        <Input type="email" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item name="address_line1" label={t('address', { defaultValue: 'Address' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="address_line2" label={t('addressLine2', { defaultValue: 'Address line 2' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="city" label={t('city', { defaultValue: 'City' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="state_name" label={t('state', { defaultValue: 'State' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="pincode" label={t('pincode', { defaultValue: 'PIN code' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'family',
                label: t('familyTab'),
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="father_name" label={t('fatherName', { defaultValue: 'Father name' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item name="father_phone" label={t('fatherPhone', { defaultValue: 'Father phone' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item name="father_email" label={t('fatherEmail', { defaultValue: 'Father email' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="father_occupation" label={t('fatherOccupation', { defaultValue: 'Father occupation' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="mother_name" label={t('motherName', { defaultValue: 'Mother name' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item name="mother_phone" label={t('motherPhone', { defaultValue: 'Mother phone' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item name="mother_email" label={t('motherEmail', { defaultValue: 'Mother email' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="mother_occupation" label={t('motherOccupation', { defaultValue: 'Mother occupation' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="guardian_name" label={t('guardianName', { defaultValue: 'Guardian name' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="guardian_relation" label={t('guardianRelation', { defaultValue: 'Guardian relation' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="guardian_phone" label={t('guardianPhone', { defaultValue: 'Guardian phone' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="guardian_email" label={t('guardianEmail', { defaultValue: 'Guardian email' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="emergency_contact_name" label={t('emergencyName', { defaultValue: 'Emergency contact' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="emergency_contact_phone" label={t('emergencyPhone', { defaultValue: 'Emergency phone' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="emergency_contact_relation" label={t('emergencyRelation', { defaultValue: 'Emergency relation' })}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'health',
                label: t('healthTab'),
                children: (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="medical_condition" label={t('medicalCondition')}>
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="allergies" label={t('allergies')}>
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="height_cm" label={t('heightCm')}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="weight_kg" label={t('weightKg')}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              ...(isEdit
                ? [
                    {
                      key: 'documents',
                      label: t('documentsTab'),
                      children: (
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          <Table
                            rowKey="id"
                            size="small"
                            pagination={false}
                            dataSource={existing.data?.data?.documents ?? []}
                            locale={{ emptyText: t('documentsEmpty') }}
                            columns={[
                              {
                                title: t('documentType'),
                                dataIndex: 'document_type',
                                width: 180,
                                render: (value: string) =>
                                  t(DOC_TYPE_I18N[value as keyof typeof DOC_TYPE_I18N] || 'docTypeOther', {
                                    defaultValue: value,
                                  }),
                              },
                              { title: t('documentTitle'), dataIndex: 'title' },
                              {
                                title: t('verification'),
                                dataIndex: 'verification_status',
                                width: 120,
                              },
                              { title: t('expiresOn'), dataIndex: 'expires_on', width: 120 },
                              {
                                title: t('documentFile'),
                                dataIndex: 'storage_ref',
                                width: 120,
                                render: (ref: string | null | undefined) =>
                                  ref ? (
                                    <a href={`${serverOrigin}${ref}`} target="_blank" rel="noreferrer">
                                      {t('documentOpen')}
                                    </a>
                                  ) : (
                                    t('documentNoFile')
                                  ),
                              },
                            ]}
                          />
                          <Card size="small" title={t('addDocument')}>
                            <Form
                              form={docForm}
                              layout="vertical"
                              initialValues={{ verification_status: 'pending' }}
                              onFinish={(values) => documentUpload.mutate(values)}
                            >
                              <Row gutter={16}>
                                <Col xs={24} md={8}>
                                  <Form.Item
                                    name="document_type"
                                    label={t('documentType')}
                                    rules={[{ required: true }]}
                                  >
                                    <Select
                                      options={STUDENT_DOCUMENT_TYPES.map((type) => ({
                                        value: type,
                                        label: t(DOC_TYPE_I18N[type]),
                                      }))}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item name="title" label={t('documentTitle')} rules={[{ required: true }]}>
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item name="verification_status" label={t('verification')}>
                                    <Select
                                      options={[
                                        { value: 'pending', label: t('verificationPending') },
                                        { value: 'verified', label: t('verificationVerified') },
                                        { value: 'rejected', label: t('verificationRejected') },
                                      ]}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item name="expires_on" label={t('expiresOn')}>
                                    <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item label={t('documentFile')}>
                                    <Upload
                                      maxCount={1}
                                      accept=".pdf,image/jpeg,image/png,image/gif,image/webp"
                                      beforeUpload={(file) => {
                                        setPendingDocFile(file);
                                        return false;
                                      }}
                                      onRemove={() => setPendingDocFile(null)}
                                      fileList={
                                        pendingDocFile
                                          ? [
                                              {
                                                uid: 'pending-doc',
                                                name: pendingDocFile.name,
                                                status: 'done',
                                              },
                                            ]
                                          : []
                                      }
                                    >
                                      <Button icon={<UploadOutlined />}>{t('documentUpload')}</Button>
                                    </Upload>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8} style={{ display: 'flex', alignItems: 'end' }}>
                                  <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={documentUpload.isPending}>
                                      {t('addDocument')}
                                    </Button>
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Form>
                          </Card>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate(isEdit && id ? `/education/students/${id}` : '/education/students')}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
